import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import HyperDX from '@hyperdx/browser';
import { skipToken } from '@reduxjs/toolkit/query';
import { useContext, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Spinner, ACCESS_LOAN_URL, APPLICATION_FINDER_URL, BREAD_LENDER_STATUS, PATH, PRE_QUALIFICATION_ACCOUNT_EXISTS_URL, PRE_QUALIFICATION_DECLINED_ADS_ONLY_URL, PRE_QUALIFICATION_DECLINED_IMPRINT_ONLY_URL, PRE_QUALIFICATION_DECLINED_URL, PRE_QUALIFICATION_INFORMATION_REQUIRED_URL, PRODUCT_TYPE, QUERY_KEY, STEP_UP_REQUIRED_URL, useIovation, useParentMessaging, useQuery, approveTerms, parseQuery, AnalyticsContext, } from 'shared';
import { ServiceProviderDisclosures, WizardDescription } from 'components';
import { useGetConsumerApplicationsV2CreditApplicationsApplicationIdLenderRequirementsQuery, usePatchConsumerApplicationsV2CreditApplicationsApplicationIdMutation, usePostConsumerApplicationsV2CreditApplicationsApplicationIdPrequalifyMutation, usePostConsumerApplicationsV2CreditApplicationsCreateMutation, } from 'consumer-applications-v2-api';
import { useSessionStorage, useABTest } from 'hooks';
import { setSubmittedOtaToken } from '../../actions';
import { AdditionalInfoForm, PrefilledPersonalInfoForm } from '../../forms';
import { getProvePreFill, getProvePreFillOTA, } from '../../prove/views/utils/api';
import { selectors as creditPortalSelectors } from '../../reducer';
import { clearProveApp, clearProveFail, clearProveVerify, setLenderReferenceId, setPersonalInfo, setPersonalInfoOTA, setPrequalification, setProveFail, } from '../actions';
import { ConfirmInfoModal, PreparingOfferModal } from '../components';
import { selectors } from '../reducer';
function PersonalInfo({ moveToNextStep, allowChangePractice, showFindApplication, }) {
    const getToken = () => {
        const query = parseQuery(location.search);
        const token = query[QUERY_KEY.TOKEN];
        return token;
    };
    //CUSTOM HOOKS
    const dispatch = useDispatch();
    const { analytics } = useContext(AnalyticsContext);
    const [cookies] = useCookies(['prove_updates_test', 'storeLinkCampaign']);
    const location = useLocation();
    const history = useHistory();
    const [getBlackboxData] = useIovation();
    const tokenRef = useRef(getToken());
    const { resetValue: resetIsOTA } = useSessionStorage('isOTA');
    const { queryObject: queryParams } = useQuery();
    const { assignABVariant, data: proveUpdatesVariant, isLoading: isLoadingProveUpdatesVariant, } = useABTest('prove_updates_test');
    const { sendApplicationCreated, sendPrequalification } = useParentMessaging();
    //REDUX STATE
    const practice = useSelector(selectors.practice);
    const personalInfo = useSelector(selectors.personalInfo);
    const personalInfoOTA = useSelector(selectors.personalInfoOTA);
    const creditApplicationOTA = useSelector(selectors.creditApplication);
    const applicationEnvironment = useSelector(selectors.applicationEnvironment);
    const proveVerify = useSelector(selectors.proveVerify);
    const proveFail = useSelector(selectors.proveFail);
    const proveBypass = useSelector(selectors.proveBypass);
    const submittedOtaToken = useSelector(creditPortalSelectors.submittedOtaToken);
    //COMPONENT STATE
    const [screen, setScreen] = useState('INITIAL INFO');
    const [applicationId, setApplicationId] = useState('');
    const [isProvePrefilled, setIsProvePrefilled] = useState(false);
    const [sessionJson] = useState(proveVerify);
    const [sessionJsonFail] = useState(proveFail);
    const [isProveLoading, setIsProveLoading] = useState(false);
    const [proveSession, setProveSession] = useState(undefined);
    const [proveSessionFail, setProveSessionFail] = useState(undefined);
    const [creditApplicationPayloadData, setCreditApplicationPayloadData] = useState(null);
    const [showPreparingOfferModal, setShowPreparingOfferModal] = useState(false);
    const [submittingForm, setSubmittingForm] = useState(false);
    const [prefillSource, setPrefillSource] = useState(null);
    const [isConfirmInfoModalOpen, setIsConfirmInfoModalOpen] = useState(false);
    const [overlappingFields, setOverlappingFields] = useState(null);
    //API Hooks
    const { data: lenderRequirements, isError: isErrorLenderRequirements } = useGetConsumerApplicationsV2CreditApplicationsApplicationIdLenderRequirementsQuery(applicationId ? { applicationId } : skipToken);
    const [createCreditApplication, { data: creditApplication, isError: isErrorCreateCreditApplication },] = usePostConsumerApplicationsV2CreditApplicationsCreateMutation();
    const [prequalifyCreditApplication, { data: prequalification, isError: isErrorPrequalify },] = usePostConsumerApplicationsV2CreditApplicationsApplicationIdPrequalifyMutation();
    const [patchCreditApplication, { data: patchedCreditApplication, isError: isErrorPatchCreditApplication },] = usePatchConsumerApplicationsV2CreditApplicationsApplicationIdMutation();
    //HANDLERS
    const submitPrequalification = async (additionalInfoFormValues) => {
        const creditApplicationToUse = creditApplication
            ? creditApplication.creditApplication
            : creditApplicationOTA;
        const { consumer } = creditApplicationToUse
            ? creditApplicationToUse.locationServiceProviderPriorities
            : { consumer: [] };
        const acPrimePriority = consumer.find(({ productType }) => productType === PRODUCT_TYPE.AC_PRIME);
        await prequalifyCreditApplication({
            applicationId,
            postConsumerApplicationsV2CreditApplicationsApplicationIdPrequalify: {
                priority: acPrimePriority?.priority,
                iovationBlackBox: creditApplicationPayloadData.iovationBlackBox,
                prerequisiteTermsAccepted: creditApplicationPayloadData.prerequisiteTermsAccepted,
                purchaseAmount: personalInfo.purchaseAmount,
                monthlyHousingPayment: additionalInfoFormValues?.monthlyHousingPayment,
                housingType: additionalInfoFormValues?.housingType,
                primaryIncomeSource: additionalInfoFormValues?.primaryIncomeSource,
                grossAnnualIncome: additionalInfoFormValues?.grossAnnualIncome,
            },
        });
    };
    const handleSubmitAdditionalInfo = async (additionalInfoFormValues) => {
        setShowPreparingOfferModal(true);
        // Ensure the modal stays open for at least 2 seconds
        const minWait = new Promise((resolve) => setTimeout(resolve, 2000));
        await Promise.all([
            minWait,
            submitPrequalification(additionalInfoFormValues),
        ]);
        setShowPreparingOfferModal(false);
    };
    const attachProveData = () => {
        const proveAbParticipantUuid = cookies.prove_updates_test?.participantUuid;
        let proveBypassToUse = undefined;
        if (proveBypass) {
            proveBypassToUse = proveBypass;
        }
        else if (proveSession || proveSessionFail) {
            proveBypassToUse = false;
        }
        return {
            proveSessionId: proveSession ?? proveSessionFail ?? undefined,
            proveBypassed: proveBypassToUse,
            ...(proveAbParticipantUuid
                ? { abParticipantUuid: proveAbParticipantUuid }
                : {}),
        };
    };
    const assembleCreditApplicationData = () => {
        const { alphaeonTerms, lenderTerms } = applicationEnvironment;
        const approvedTerms = approveTerms(alphaeonTerms, lenderTerms);
        const blackboxData = getBlackboxData();
        const { consumer } = creditApplication?.creditApplication?.locationServiceProviderPriorities ||
            {};
        const acPrimePriority = consumer
            ? consumer.find(({ productType }) => productType === PRODUCT_TYPE.AC_PRIME)
            : null;
        const partnerTrackingGuid = queryParams.get('partner_tracking_guid');
        const partnerIdentifier = queryParams.get('partner_identifier');
        const storeLinkCampaign = cookies.storeLinkCampaign;
        return {
            iovationBlackBox: blackboxData.blackbox,
            prerequisiteTermsAccepted: approvedTerms,
            ...(partnerTrackingGuid ? { partnerTrackingGuid } : {}),
            ...(partnerIdentifier ? { partnerIdentifier } : {}),
            ...(acPrimePriority ? { priority: acPrimePriority } : {}),
            ...(storeLinkCampaign
                ? {
                    storeLinkCampaign: storeLinkCampaign.campaignId,
                    otl: storeLinkCampaign.otl,
                }
                : {}),
            ...attachProveData(),
        };
    };
    const handleSubmit = async (prequalificationFormValues) => {
        dispatch(setPersonalInfo(prequalificationFormValues));
        const payloadData = assembleCreditApplicationData();
        setCreditApplicationPayloadData(payloadData);
        HyperDX.setGlobalAttributes({
            locationId: practiceToUse?.locationId.toString() || '',
            storeNumber: practiceToUse?.adsStoreNumber.toString() || '',
            creditApplicationId: creditApplication?.creditApplication?.applicationId?.toString() || '',
            lastName: prequalificationFormValues.lastName,
            firstName: prequalificationFormValues.firstName,
            email: prequalificationFormValues.email,
            phone: prequalificationFormValues.primaryPhone,
            form: 'personal-info-v2',
        });
        if (!creditApplicationOTA) {
            //IF NOT AN OTA
            await createCreditApplication({
                postConsumerApplicationsV2CreditApplicationsCreate: {
                    ...prequalificationFormValues,
                    ...payloadData,
                    ...(practiceToUse ? { locationId: practiceToUse.locationId } : {}),
                },
            });
        }
        else {
            //IF AN OTA
            await patchCreditApplication({
                applicationId: creditApplicationOTA.applicationId,
                patchConsumerApplicationsV2CreditApplicationsApplicationId: {
                    ...prequalificationFormValues,
                    ...payloadData,
                    ...(practiceToUse ? { locationId: practiceToUse.locationId } : {}),
                },
            });
        }
    };
    // MISC JS
    const practiceFromState = location.state;
    const practiceToUse = !practice?.locationId && practiceFromState?.practice?.locationId
        ? practiceFromState.practice
        : practice;
    const initializeProve = async () => {
        setIsProveLoading(true);
        let getPreFillData;
        if (sessionJson && sessionJson.session_id === null) {
            getPreFillData = getProvePreFillOTA;
        }
        else if (sessionJson && sessionJson.session_id) {
            getPreFillData = getProvePreFill;
        }
        if (sessionJson && getPreFillData) {
            const preFillJson = await getPreFillData(sessionJson);
            const preFillRes = await preFillJson?.json();
            setProveSession(sessionJson.session_id ?? preFillRes.session_id); // Set Prove Session no matter what
            if (preFillRes?.message !== 'Prove pre-fill already used.' &&
                preFillRes?.message !== 'No CRM data available' &&
                preFillRes?.error_code === undefined) {
                setIsProvePrefilled(true); // Set Prove Bool
                dispatch(clearProveFail());
                dispatch(setPersonalInfo({
                    street1: preFillRes.address.address,
                    street2: preFillRes.address.address_extended,
                    city: preFillRes.address.city,
                    dateOfBirth: preFillRes.date_of_birth,
                    email: preFillRes.email,
                    firstName: preFillRes.first_name,
                    lastName: preFillRes.last_name,
                    primaryPhone: sessionJson.phone_number,
                    ...(preFillRes.ssn_last_4
                        ? { ssn: `XXXXX${preFillRes.ssn_last_4}` }
                        : {}),
                    state: preFillRes.address.region,
                    zip: preFillRes.address.zipcode,
                }));
            }
            else {
                setIsProveLoading(false);
                dispatch(setProveFail({
                    ...sessionJson,
                }));
                dispatch(clearProveVerify());
                dispatch(clearProveApp());
                dispatch(setPersonalInfo({
                    primaryPhone: (sessionJson && sessionJson.phone_number) ?? '',
                }));
                // Redirect if no prefill available or error
                const searchParams = new URLSearchParams(location.search);
                searchParams.set('ref', 'pi');
                history.push({
                    pathname: '/credit-portal/apply/wizard',
                    search: searchParams.toString(),
                });
            }
        }
        setProveSessionFail(sessionJson?.session_id ?? sessionJsonFail?.session_id); // Set Prove Session ID no matter what
        setIsProveLoading(false);
    };
    // Effects
    useEffect(() => {
        analytics?.track('WizardSessionStart', {});
        if (PATH.PROVE !== null) {
            initializeProve();
        }
        // If the user presses back after submitting an OTA application, reroutes to the application finder
        if (submittedOtaToken && tokenRef.current === submittedOtaToken) {
            history.push(APPLICATION_FINDER_URL);
        }
        return () => {
            resetIsOTA();
        };
    }, []);
    useEffect(() => {
        if (creditApplication &&
            creditApplication.creditApplication?.applicationId) {
            const appId = creditApplication.creditApplication.applicationId;
            setApplicationId(appId);
            sendApplicationCreated({
                applicationId: appId,
                partnerTrackingGuid: queryParams.get('partner_tracking_guid'),
            });
        }
        else if (patchedCreditApplication && creditApplicationOTA) {
            const appId = creditApplicationOTA.applicationId;
            setApplicationId(appId);
            sendApplicationCreated({
                applicationId: appId,
                partnerTrackingGuid: queryParams.get('partner_tracking_guid'),
            });
        }
    }, [creditApplication, patchedCreditApplication]);
    useEffect(() => {
        if (lenderRequirements) {
            if (lenderRequirements.collectAdditionalInfo) {
                setScreen('ADDITIONAL INFO');
            }
            else {
                submitPrequalification();
            }
        }
    }, [lenderRequirements]);
    useEffect(() => {
        if (isErrorCreateCreditApplication ||
            isErrorLenderRequirements ||
            isErrorPatchCreditApplication ||
            isErrorPrequalify) {
            setSubmittingForm(false);
        }
    }, [
        isErrorCreateCreditApplication,
        isErrorLenderRequirements,
        isErrorPatchCreditApplication,
        isErrorPrequalify,
    ]);
    useEffect(() => {
        if (prequalification && !showPreparingOfferModal) {
            dispatch(setPrequalification(prequalification));
            setSubmittingForm(false);
            const { applicationStatus, applicationServiceProviderPriority, lenderNextStepUrl, lenderStatusMessage, lenderRequestReferenceId, offers, } = prequalification;
            dispatch(setLenderReferenceId(lenderRequestReferenceId));
            const prequalifiedOffer = offers?.find((offer) => offer.offerType === 'prequalified');
            sendPrequalification({
                applicationId,
                partnerTrackingGuid: queryParams.get('partner_tracking_guid'),
                status: applicationStatus,
                requestedAmount: personalInfo.purchaseAmount
                    ? Number(personalInfo.purchaseAmount)
                    : null,
                prequalifiedAmount: prequalifiedOffer?.amount ?? null,
            });
            if (tokenRef.current) {
                // If OTA, set the token in redux
                dispatch(setSubmittedOtaToken(tokenRef.current));
            }
            sessionStorage.setItem('submittedWithProve', isProvePrefilled ? 'true' : 'false');
            analytics?.track('SubmitApplication', {});
            if (isProvePrefilled) {
                dispatch(clearProveFail()); // Clear Prove Fail
                dispatch(clearProveVerify()); // Clear Prove Verify
            }
            const adsOffer = offers?.find((offer) => offer.serviceProviderName === 'ADS');
            const imprintOffer = offers?.find((offer) => offer.serviceProviderName === 'Imprint');
            if (applicationStatus === 'prequalified') {
                moveToNextStep(creditApplicationPayloadData);
            }
            else if (applicationStatus === 'prequalified_account_exists') {
                history.push(PRE_QUALIFICATION_ACCOUNT_EXISTS_URL);
            }
            else if (applicationStatus === 'pending' &&
                lenderStatusMessage === BREAD_LENDER_STATUS.STEP_UP_NEEDED) {
                history.push(STEP_UP_REQUIRED_URL);
            }
            else if (applicationStatus === 'pending') {
                history.push(PRE_QUALIFICATION_INFORMATION_REQUIRED_URL);
            }
            else if (applicationStatus === 'pending_approval') {
                if (imprintOffer && imprintOffer.offerType === 'step_up_pend') {
                    moveToNextStep(creditApplicationPayloadData);
                }
                else {
                    history.push({
                        pathname: ACCESS_LOAN_URL,
                        state: {
                            lenderNextStepUrl,
                            modalType: 'prequal',
                        },
                    });
                }
            }
            else {
                if (adsOffer) {
                    const statusMessage = adsOffer?.offerType === 'disqualified'
                        ? 'DISQUALIFIED'
                        : 'NOT PREQUALIFIED';
                    history.push({
                        pathname: PRE_QUALIFICATION_DECLINED_ADS_ONLY_URL,
                        state: {
                            lenderStatusMessage: statusMessage,
                        },
                    });
                }
                else if (imprintOffer) {
                    history.push({
                        pathname: PRE_QUALIFICATION_DECLINED_IMPRINT_ONLY_URL,
                        state: { email: personalInfo.email, aanUrl: imprintOffer.aanUrl },
                    });
                }
                else if (applicationServiceProviderPriority?.productType === 'installment') {
                    history.push(PRE_QUALIFICATION_DECLINED_URL);
                }
                else {
                    history.push({
                        pathname: PRE_QUALIFICATION_DECLINED_ADS_ONLY_URL,
                        state: {
                            lenderStatusMessage,
                        },
                    });
                }
            }
        }
    }, [prequalification, showPreparingOfferModal]);
    useEffect(() => {
        if (personalInfoOTA &&
            proveVerify &&
            proveUpdatesVariant?.variant?.name === 'treatment') {
            const otaInfo = {
                firstName: personalInfoOTA.firstName,
                lastName: personalInfoOTA.lastName,
                dateOfBirth: personalInfoOTA.dateOfBirth,
            };
            const proveInfo = {
                firstName: personalInfo.firstName,
                lastName: personalInfo.lastName,
                dateOfBirth: personalInfo.dateOfBirth,
            };
            const isDateOfBirthSame = otaInfo.dateOfBirth === proveInfo.dateOfBirth;
            if (JSON.stringify(otaInfo) !== JSON.stringify(proveInfo)) {
                setOverlappingFields({
                    otaFields: otaInfo,
                    proveFields: proveInfo,
                });
            }
            // Don't show modal if one name is blank and DOB matches, go with prove prefill
            if ((!otaInfo.firstName || !otaInfo.lastName) && isDateOfBirthSame) {
                dispatch(setPersonalInfoOTA(undefined));
                setPrefillSource('Prove');
            }
            else {
                setIsConfirmInfoModalOpen(true);
            }
        }
    }, [
        personalInfoOTA,
        proveVerify,
        personalInfo,
        dispatch,
        proveUpdatesVariant,
    ]);
    useEffect(() => {
        if (isProvePrefilled) {
            setPrefillSource('Prove');
        }
        else if (!isProvePrefilled && personalInfoOTA) {
            setPrefillSource('OTA');
        }
    }, [isProvePrefilled, personalInfoOTA]);
    useEffect(() => {
        assignABVariant();
    }, [assignABVariant]);
    return (_jsxs(_Fragment, { children: [_jsx(PreparingOfferModal, { isOpen: showPreparingOfferModal, setIsOpen: setShowPreparingOfferModal }), overlappingFields && isConfirmInfoModalOpen && (_jsx(ConfirmInfoModal, { isOpen: isConfirmInfoModalOpen, setIsOpen: setIsConfirmInfoModalOpen, overlappingFields: overlappingFields, setIsProvePrefilled: setIsProvePrefilled, setPrefillSource: setPrefillSource })), screen === 'INITIAL INFO' && (_jsxs("div", { className: "application-instructions", children: [_jsx(WizardDescription, { locationName: practiceToUse?.name, showPracticeInfo: !!(practiceToUse &&
                            practiceToUse.name &&
                            practiceToUse.locationId &&
                            !window.location.host.includes('iframe')), allowChangePractice: allowChangePractice, otaInfo: creditApplicationOTA && personalInfo
                            ? {
                                firstName: personalInfo.firstName,
                                lastName: personalInfo.lastName,
                            }
                            : undefined, showFindApplication: showFindApplication }), !isLoadingProveUpdatesVariant && proveUpdatesVariant ? (_jsx(PrefilledPersonalInfoForm, { data: personalInfo, handleSubmit: handleSubmit, practice: practice, isProvePrefilled: isProvePrefilled, isProveLoading: isProveLoading, fieldsToExclude: applicationEnvironment?.fieldsToExclude, showAdditionalTerms: applicationEnvironment?.showAdditionalTerms, isSubmittingForm: submittingForm, setIsSubmittingForm: setSubmittingForm, prefillSource: prefillSource, proveUpdatesVariant: proveUpdatesVariant })) : (_jsx(Spinner, {})), _jsx(ServiceProviderDisclosures, {})] })), screen === 'ADDITIONAL INFO' && (_jsx(_Fragment, { children: _jsx(AdditionalInfoForm, { handleSubmit: handleSubmitAdditionalInfo }) }))] }));
}
export default PersonalInfo;
